<template>
  <!-- eslint-disable vue/no-deprecated-v-bind-sync -->
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-4 left">
          <div class="title">
            {{ $t('sidebar.purchase') }} > {{ $t('sidebar.purchaseapprover') }}
          </div>
        </div>
        <div class="col-md-8 right">
          <div class="action">
            <base-dropdown-filter-download
              :items="DOWNLOAD_EXTS"
              :loading="false"
              @onSelect="handleSelectDownload"
            />
            <base-filter-data-type
              :type="dataType"
              :items="REPORT_DATA_TYPES"
              @onSelect="handleSelectDataType"
            />
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: dateStart, end: dateEnd }"
              :active="isAppliedFilter"
              @onSubmit="handleSubmitFilter"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <div
        v-else
        class="col-md-12 no-padding mt-2">
        <template v-if="purchaseData.length">
          <section class="mt-2 mb-4">
            <div>
              <section-title
                :title="datePeriod(dateStart, dateEnd)"
                fontweight="bold"/>
            </div>
          </section>
          <keep-alive>
            <ReportGeneralTable
              v-if="dataType === REPORT_DATA_TYPE_VALUE.TABLE"
              :items="purchaseData"
              :columnNames="columnNames"
              :pickKeys="pickKeys"
            />
            <div v-else>
              <div class="row mb-4">
                <div
                  class="col-2 d-flex justify-content-center d-flex align-items-center m-auto"
                  style="min-width: 267px;">
                  <select
                    v-model="selectedKey"
                    class="form-control custom-select">
                    <option
                      v-for="(key, index) in keys"
                      :key="index"
                      :value="key"
                    >
                      {{ getTextByKey(key) }}
                    </option>
                  </select>
                </div>
              </div>
              <ReportPurchaseDataChart
                v-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_BAR"
                :items="purchaseData"
                :selectedKey="selectedKey"
                type="bar"
              />
              <ReportPurchaseDataChart
                v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_LINE"
                :items="purchaseData"
                :selectedKey="selectedKey"
                type="line"
              />
              <ReportPurchaseDataChart
                v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_PIE"
                :items="purchaseData"
                :selectedKey="selectedKey"
                type="pie"
              />
            </div>
          </keep-alive>
        </template>
        <template v-else>
          {{ $t('message.nodatafound') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE, REPORT_DATA_TYPE_ICON, REPORT_DATA_TYPE_TEXT } from '../../../../helpers/enum'
import { getDateStart, getDateEnd, getLastSevenDay, getMasterDataText } from '../../../../helpers'
import sidebarModule from '../../sidebarModule'
import XLSX from 'xlsx'
import { getFileName } from '@/helpers/exportFormater'
import userPermissionMixin from '@/mixins/userPermission'
import ReportGeneralTable from '../../../combine/ReportGeneralTable.vue'
import ReportPurchaseDataChart from '../../../combine/ReportPurchaseDataChart.vue'

const LAST_SEVEN_DAY = getLastSevenDay()

export default {
  components: {
    ReportGeneralTable,
    ReportPurchaseDataChart,
  },

  mixins: [userPermissionMixin],

  data () {
    return {
      REPORT_DATA_TYPES: [
        ...REPORT_DATA_TYPES,
        {
          icon: REPORT_DATA_TYPE_ICON.CHART_PIE,
          text: REPORT_DATA_TYPE_TEXT.CHART_PIE,
          value: REPORT_DATA_TYPE_VALUE.CHART_PIE,
        },
      ],
      REPORT_DATA_TYPE_VALUE,
      DOWNLOAD_EXTS,
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      dateStart: LAST_SEVEN_DAY.START,
      dateEnd: LAST_SEVEN_DAY.END,
      dataType: REPORT_DATA_TYPE_VALUE.TABLE,
      chartSelectedLabels: {},
      isAppliedFilter: false,
      selectedKey: 'grandTotal',
    }
  },

  computed: {
    hasAnyTax() {
      for (const data of this.$store.state.report.purchaseData) {
        for (const item of data.items) {
          if (item.taxTotal > 0) {
            return true
          }
        }
      }
      return false
    },
    hasAnyDiscount() {
      for (const data of this.$store.state.report.purchaseData) {
        for (const item of data.items) {
          if (item.discountTotal > 0) {
            return true
          }
        }
      }
      return false
    },
    purchaseData () {
      return this.$store.state.report.purchaseData.map((item) => {
        return {
          ...item,
          date: (() => {
            if (this.dateType === FILTER_DATE_TYPE.DAY) {
              return this.getMyLocalDate(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.MONTH) {
              return this.getMyLocalMonthYear(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.YEAR) {
              return this.getMyLocalYear(item.date, { short: false })
            }
            return item.date
          })(),
          skuQuantity: item.items.reduce((acc, cur) => acc + cur.skuQuantity, 0),
          discountTotal: item.items.reduce((acc, cur) => acc + cur.discountTotal, 0),
          subTotal: item.items.reduce((acc, cur) => acc + cur.subTotal, 0),
          taxTotal: item.items.reduce((acc, cur) => acc + cur.taxTotal, 0),
          grandTotal: item.items.reduce((acc, cur) => acc + cur.grandTotal, 0),
          items: item.items.map((item) => {
            return {
              ...item,
              info: {
                fieldName: item.fieldName,
              },
            }
          }),
        }
      })
    },
    columnNames() {
      return [
        {
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.quantity_sku'),
        },
        ...(() => {
          if (this.hasAnyTax || this.hasAnyDiscount) {
            return [{
              style: { 'width': '140px' },
              text: this.$t('apps.reports.message.subtotal'),
            }]
          }
          return []
        })(),
        ...(() => {
          if (this.hasAnyDiscount) {
            return [{
              style: { 'width': '140px' },
              text: this.$t('apps.reports.message.discounttotal'),
            }]
          }
          return []
        })(),
        ...(() => {
          if (this.hasAnyTax) {
            return [{
              style: { 'width': '140px' },
              text: this.$t('apps.reports.message.taxtotal'),
            }]
          }
          return []
        })(),
        {
          style: { 'width': '100px' },
          text: this.$t('apps.reports.message.grandtotal'),
        },
      ].filter((v) => v)
    },
    pickKeys() {
      return [
        'skuQuantity',
        this.hasAnyTax || this.hasAnyDiscount ? 'subTotal' : null,
        this.hasAnyDiscount ? 'discountTotal' : null,
        this.hasAnyTax ? 'taxTotal' : null,
        'grandTotal',
      ].filter((v) => v)
    },
    keys() {
      return [
        'skuQuantity',
        this.hasAnyTax || this.hasAnyDiscount ? 'subTotal' : null,
        this.hasAnyDiscount ? 'discountTotal' : null,
        this.hasAnyTax ? 'taxTotal' : null,
        'grandTotal',
      ].filter((v) => v)
    },
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.reports.title'),
      current: {
        app_name: 'reports',
        app_id: 8,
        module_id: 2,
        submodule_id: 4,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 2,
      submenuAtIdx: 3,
    })
  },

  async created () {
    this.fetchData()
  },

  destroyed: function () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.ready = false
      await this.$store.dispatch('getReportPurchaseData', {
        path: 'approvers',
        params: {
          'filter[date_start]': getDateStart(this.dateStart),
          'filter[date_end]': getDateEnd(this.dateEnd),
          'filter[date_type]': this.dateType,
          'timezone': moment.tz.guess(),
          'purchase[type]': 'po',
        },
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handleSubmitFilter ({ type, start, end }) {
      this.dateType = type
      this.dateStart = start
      this.dateEnd = end
      this.isAppliedFilter = true
      this.fetchData()
    },
    handleSelectDataType ({ value = null }) {
      this.dataType = value
    },
    handleSelectDownload ({ value = null }) {
      if (value === DOWNLOAD_EXT_VALUE.EXCEL) {
        this.exportAs('xlsx')
      } else if (value === DOWNLOAD_EXT_VALUE.CSV) {
        this.exportAs('csv')
      }
    },
    async exportAs (fileType) {
      /* convert state to workbook */
      const headColumns = [
        this.$t('message.date'),
        this.$t('sidebar.purchaseapprover'),
        this.$t('apps.reports.message.quantity_sku'),
        this.hasAnyTax || this.hasAnyDiscount ? this.$t('apps.reports.message.subtotal') : '{hiden}',
        this.hasAnyDiscount ? this.$t('apps.reports.message.discounttotal') : '{hiden}',
        this.hasAnyTax ? this.$t('apps.reports.message.taxtotal') : '{hiden}',
        this.$t('apps.reports.message.grandtotal'),
      ].filter((v) => v !== '{hiden}')

      const bodyRows = this.$store.state.report.purchaseData.reduce((acc, cur) => {
        const results = cur.items.map((item) => (
          [
            cur.date,
            item.fieldName,
            item.skuQuantity,
            this.hasAnyTax || this.hasAnyDiscount ? item.subTotal : '{hiden}',
            this.hasAnyDiscount ? item.discountTotal : '{hiden}',
            this.hasAnyTax ? item.taxTotal : '{hiden}',
            item.grandTotal,
          ].filter((v) => v !== '{hiden}')
        ))
        acc = [
          ...acc,
          ...results,
        ]
        return acc
      }, [])
      const data = [headColumns, ...bodyRows]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet')

      /* generate file and send to client */
      const fileName = getFileName(this.$t, fileType, 'sidebar.purchase', 'sidebar.purchaseapprover')

      XLSX.writeFile(wb, fileName)
    },
    getTextByKey (key) {
      const patt = {
        'skuQuantity': this.$t('apps.reports.message.quantity_sku'),
        'subTotal': this.$t('apps.reports.message.subtotal'),
        'discountTotal': this.$t('apps.reports.message.discounttotal'),
        'taxTotal': this.$t('apps.reports.message.taxtotal'),
        'grandTotal': this.$t('apps.reports.message.grandtotal'),
      }
      return patt[key]
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/report/purchase/Approver.vue